/** Health endpoint viewer (spec §121). */
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, XCircle } from "lucide-react";

import { api } from "@/api/client";

interface HealthPayload {
  ok: boolean;
  checks: Record<string, { ok: boolean; ms?: number; error?: string;
                           write_grants_present?: string[];
                           sources?: Record<string, unknown> }>;
}

export function SystemHealth() {
  const { data, isLoading } = useQuery({
    queryKey: ["health"],
    queryFn: async () => (await api.get<HealthPayload>("/health")).data,
    refetchInterval: 30_000,
  });

  if (isLoading) return <div className="skeleton h-40" />;

  return (
    <div className="space-y-4">
      <h1 className="font-display font-bold text-[24px] tracking-tight">System health</h1>

      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
        {Object.entries(data?.checks ?? {}).map(([name, check]) => (
          <div key={name} className="card p-4">
            <div className="flex items-center gap-2">
              {check.ok
                ? <CheckCircle2 size={16} className="text-good" />
                : <XCircle size={16} className="text-bad" />}
              <span className="font-display font-semibold text-[15px]">{name}</span>
              {check.ms != null && (
                <span className="ml-auto font-mono text-[12px] text-ink-muted tnum">{check.ms}ms</span>
              )}
            </div>
            {check.error && <p className="text-[12.5px] text-bad mt-1.5">{check.error}</p>}
            {check.write_grants_present && check.write_grants_present.length > 0 && (
              <p className="text-[12.5px] text-bad mt-1.5">
                Source connection holds write grants: {check.write_grants_present.join(", ")}.
                The read-only guarantee is not in force.
              </p>
            )}
            {check.sources && (
              <ul className="text-[12px] font-mono text-ink-muted mt-2 space-y-0.5">
                {Object.entries(check.sources).map(([source, info]) => (
                  <li key={source} className="flex justify-between gap-2">
                    <span>{source}</span>
                    <span>{(info as { status: string }).status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
